// Vokshus cart cross-sell (rev27) — honest ladder upsell.
// Two button shapes are supported:
//   • SWAP  (data-variant-id + data-vk-remove-id): adds the higher kit tier,
//     then decrements the lower-tier line by ONE — a customer with qty≥2
//     keeps the extra units (zeroing the line silently deleted them).
//   • ADD   (data-vk-add-handle, no remove): adds an accessory. The accessory
//     product ids are created at deploy and unknown to the theme, so we resolve
//     the handle → first variant id via /products/{handle}.js at click time.
//     data-vk-selling-plan (optional) adds as SUBSCRIPTION — /cart/add.js
//     accepts selling_plan; the Subscriptions widget/permalink does NOT work
//     via AJAX (verified 2026-07-06). Used by the bivokspapir abo-first card.
// Add-first/remove-second: a mid-flight failure leaves both lines (visible
// on refresh) rather than an empty cart.
// NB: no automatic discounts — pricing comes from the kit variant itself, so
// upgrades must SWAP variants, never add a duplicate lower-tier line.
//
// AUTOMATISKT PAKETBYTE (prisrevisionen 2026-09-24, Elias val «Korgen byter själv»):
// trappa-knappen bär data-vk-auto-swap när paketet är billigare än de lösa påsarna i
// riktiga priser (räknat i vokshus-cart-upsell.liquid). Då klickar vi den själva EN
// gång per sidvisning och antal, och visar sparet (data-vk-auto-note) överst i korgen.
// data-vk-remove-all nollar ALLA rader med den lösa påsen via radnyckel: påsar från
// produktsidan och från «Ekstra brødpose» kan ligga på två rader, och en uppdatering
// per variant-id rörde bara den första (då låg paketet PLUS en lös påse kvar).
//
// Refresh-strategi: efter add hämtar vi cart-items-component:s aktuella
// section-HTML via Shopify Section Rendering API (?section_id=...) och
// byter innerHTML in-place. Skickar sedan 'cart:update'-event så Horizons
// cart-icon uppdaterar antal-bubblan. Fallback till full reload om något
// går snett — bättre att laddas om än att visa stale state.

(() => {
  // Mønster-namn → kanonisk engelska för SpeedFulfill (speglar
  // vokshus-fulfillment-packing.js; okänt värde lämnas oförändrat).
  // 🔴 Denne tabellen MÅ være identisk med MAP i vokshus-fulfillment-packing.js.
  // scripts/test-fulfillment-packing.js sammenligner dem og feiler hvis de sklir
  // fra hverandre. «citron» er Lemon, ikke Citrus: se begrunnelsen i den filen.
  const PATTERN_EN = {
    'sitrus': 'Citrus', 'citrus': 'Citrus',
    'honning': 'Honey', 'honey': 'Honey', 'honung': 'Honey',
    'jordbær': 'Strawberry', 'jordbaer': 'Strawberry', 'strawberry': 'Strawberry',
    'jordgubbe': 'Strawberry', 'jordgubbar': 'Strawberry',
    'granateple': 'Pomegranate', 'pomegranate': 'Pomegranate',
    'granatäpple': 'Pomegranate', 'granatapple': 'Pomegranate', 'granatæble': 'Pomegranate',
    // Seks nye mønstre 2026-08-09. Rekkefølgen per rad er nb, en, sv, deretter
    // ascii-varianter av de nordiske tegnene, fordi variant-titler har blitt
    // transkribert uten ø og ö før.
    'bikube': 'Beehive', 'beehive': 'Beehive', 'bikupa': 'Beehive',
    'zero waste': 'Zero Waste', 'zerowaste': 'Zero Waste',
    'jungel': 'Jungle', 'jungle': 'Jungle', 'djungel': 'Jungle',
    'sitrusskiver': 'Citrus Slices', 'citrus slices': 'Citrus Slices',
    'citrusskivor': 'Citrus Slices',
    'sitron': 'Lemon', 'lemon': 'Lemon', 'citron': 'Lemon',
    'grønt kjøkken': 'Green Kitchen', 'gront kjokken': 'Green Kitchen',
    'green kitchen': 'Green Kitchen',
    'grönt kök': 'Green Kitchen', 'gront kok': 'Green Kitchen',
    // DANSK (/da-dk), tillagt 2026-08-21. Honning, Jordbær, Bikube og Zero Waste
    // har INGEN dansk rad i oversettelses-DB-en og faller tilbake på bokmål — de
    // står allerede over. «Grønt køkken» hadde allerede lekket til SpeedFulfill
    // uoversatt på ordre #1357 (2026-08-20) da denne raden ble lagt inn.
    'granataeble': 'Pomegranate',
    'citrusskiver': 'Citrus Slices',
    'grønt køkken': 'Green Kitchen', 'gront kokken': 'Green Kitchen',
    // NEDERLANDSK (/nl-eu), tillagt 2026-08-21. Citrus, Zero Waste og Jungle er
    // identiske med engelsk. Ordre #1359 gikk uten mønster fordi nedtrekket viste
    // «Honing».
    'honing': 'Honey',
    'aardbei': 'Strawberry',
    'granaatappel': 'Pomegranate',
    'bijenkorf': 'Beehive',
    'citrusschijfjes': 'Citrus Slices',
    'citroen': 'Lemon',
    'groene keuken': 'Green Kitchen',
    // TYSK (/de-eu og /de-intl), tillagt 2026-08-21. Zero Waste er uoversatt og
    // står allerede over. Både u- og ue-transkripsjonen av ü ligger med: en miss
    // her er taus.
    'zitrus': 'Citrus',
    'honig': 'Honey',
    'erdbeere': 'Strawberry',
    'granatapfel': 'Pomegranate',
    'bienenstock': 'Beehive',
    'dschungel': 'Jungle',
    'zitrusscheiben': 'Citrus Slices',
    'zitrone': 'Lemon',
    'grüne küche': 'Green Kitchen', 'grune kuche': 'Green Kitchen',
    'gruene kueche': 'Green Kitchen',
    // FRANSK (/fr-eu), tillagt 2026-08-21. Citron er Lemon på fransk også og står
    // allerede over. 🔴 Sitrusskiver har TO franske navn i drift: oversettelses-DB-en
    // sier «Rondelles d'agrumes» (variant-tittelen kunden faktisk sender), mens
    // locales/fr.json sier «Tranches d'agrumes» (etiketten i mønster-listene).
    // BEGGE må stå her, i rak og typografisk apostrof, til den ene er borte.
    'agrumes': 'Citrus',
    'miel': 'Honey',
    'fraise': 'Strawberry',
    'grenade': 'Pomegranate',
    'ruche': 'Beehive',
    "rondelles d'agrumes": 'Citrus Slices', 'rondelles d’agrumes': 'Citrus Slices',
    "tranches d'agrumes": 'Citrus Slices', 'tranches d’agrumes': 'Citrus Slices',
    'cuisine verte': 'Green Kitchen',
    // Avocado replacement and new roll patterns, all seven customer languages.
    "avokado": "Avocado",
    "avocado": "Avocado",
    "avocat": "Avocado",
    "store blader": "Large leaves",
    "large leaves": "Large leaves",
    "stora blad": "Large leaves",
    "store blade": "Large leaves",
    "große blätter": "Large leaves",
    "grandes feuilles": "Large leaves",
    "grote bladeren": "Large leaves",
    "grønn hverdag": "Green living",
    "green living": "Green living",
    "grön vardag": "Green living",
    "grøn hverdag": "Green living",
    "grüner alltag": "Green living",
    "quotidien vert": "Green living",
    "groen leven": "Green living",
    "vannmelon": "Watermelon",
    "watermelon": "Watermelon",
    "vattenmelon": "Watermelon",
    "vandmelon": "Watermelon",
    "wassermelone": "Watermelon",
    "pastèque": "Watermelon",
    "watermeloen": "Watermelon",
    "sitrusfrukt": "Watermelon",
    "citrus fruit": "Watermelon",
    "citrusfrukt": "Watermelon",
    "citrusfrugt": "Watermelon",
    "citrusvruchten": "Watermelon",
    "zitrusfrüchte": "Watermelon",
    "mélange d'agrumes": "Watermelon",
    // Bivoksposer (Inkorgen 105, 26/10): fem mønster, sju språk, samma namn som Translate & Adapt.
    "brød og croissant": "Bread and croissant",
    "bread and croissant": "Bread and croissant",
    "bröd och croissant": "Bread and croissant",
    "brood en croissant": "Bread and croissant",
    "brot und croissant": "Bread and croissant",
    "pain et croissant": "Bread and croissant",
    "brød og kjevle": "Bread and rolling pin",
    "bread and rolling pin": "Bread and rolling pin",
    "bröd och kavel": "Bread and rolling pin",
    "brød og kagerulle": "Bread and rolling pin",
    "brood en deegroller": "Bread and rolling pin",
    "brot und nudelholz": "Bread and rolling pin",
    "pain et rouleau": "Bread and rolling pin",
    "kirsebær": "Cherry",
    "cherry": "Cherry",
    "körsbär": "Cherry",
    "kers": "Cherry",
    "kirsche": "Cherry",
    "cerise": "Cherry",
    "oliven": "Olive",
    "olive": "Olive",
    "oliver": "Olive",
    "olijf": "Olive",
    "regnbue": "Rainbow",
    "rainbow": "Rainbow",
    "regnbåge": "Rainbow",
    "regenboog": "Rainbow",
    "regenbogen": "Rainbow",
    "arc-en-ciel": "Rainbow",
    "cytrusy": "Citrus",
    "owoce cytrusowe": "Watermelon",
    "citrusové ovoce": "Watermelon",
    "miód": "Honey",
    "med": "Honey",
    "truskawki": "Strawberry",
    "jahody": "Strawberry",
    "granat": "Pomegranate",
    "granátové jablko": "Pomegranate",
    "včelín": "Beehive",
    "dżungla": "Jungle",
    "džungle": "Jungle",
    "plaster miodu": "Beehive",
    "plastry cytrusów": "Citrus Slices",
    "plátky citrusů": "Citrus Slices",
    "cytryna": "Lemon",
    "zielona kuchnia": "Green Kitchen",
    "zelená kuchyň": "Green Kitchen",
    "awokado": "Avocado",
    "avokádo": "Avocado",
    "duże liście": "Large leaves",
    "velké listy": "Large leaves",
    "zielona codzienność": "Green living",
    "zelený všední den": "Green living",
    "chleb i rogalik": "Bread and croissant",
    "chléb a croissant": "Bread and croissant",
    "chleb i wałek": "Bread and rolling pin",
    "chléb a váleček": "Bread and rolling pin",
    "wiśnie": "Cherry",
    "třešně": "Cherry",
    "oliwki": "Olive",
    "olivy": "Olive",
    "tęcza": "Rainbow",
    "duha": "Rainbow"
  };
  const VARIANT_MARKER = /;\s*pattern\s*=\s*variant title/i;

  // Byt pekaren "; pattern = variant title" mot det faktiskt valda mønstret.
  function resolvePackingList(base, variantTitle) {
    if (!base || !VARIANT_MARKER.test(base)) return base;
    const t = String(variantTitle || '').trim();
    if (!t) return base.replace(VARIANT_MARKER, '');
    return base.replace(VARIANT_MARKER, ` — Pattern: ${PATTERN_EN[t.toLowerCase()] || t}`);
  }

  function findCartContainer() {
    return document.querySelector('cart-items-component[data-section-id]');
  }

  async function refreshSection(container) {
    const sectionId = container.dataset.sectionId;
    if (!sectionId) return false;

    const [sectionRes, cartRes] = await Promise.all([
      fetch(`${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`, {
        credentials: 'same-origin',
      }),
      fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      }),
    ]);
    if (!sectionRes.ok || !cartRes.ok) return false;

    const [html, cart] = await Promise.all([sectionRes.text(), cartRes.json()]);
    const newDoc = new DOMParser().parseFromString(html, 'text/html');
    const newComp = newDoc.querySelector('cart-items-component');
    if (!newComp) return false;

    container.innerHTML = newComp.innerHTML;

    // Horizon-idiomatic cart event — cart-icon + cart-drawer listens.
    // Vi använder strängen 'cart:update' direkt (= ThemeEvents.cartUpdate)
    // så vi slipper modul-import från en non-module script-fil.
    container.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'vokshus-cart-upsell',
          data: {
            source: 'vokshus-cart-upsell',
            itemCount: cart.item_count,
          },
        },
      })
    );
    return true;
  }

  function init() {
    document.body.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-vk-cart-upsell-btn]');
      if (!btn) return;
      e.preventDefault();
      const removeId = btn.dataset.vkRemoveId;
      // Antal enheter som ska bytas ut (trappa-swappen byter ALLA N lösa poser
      // mot N-pakken; kit-upgrades byter 1). Default 1.
      const removeQty = parseInt(btn.dataset.vkRemoveQty || '1', 10);
      const addHandle = btn.dataset.vkAddHandle;
      let variantId = btn.dataset.variantId;
      if (!variantId && !addHandle) return;

      const originalText = btn.textContent;
      btn.disabled = true;
      // i18n: busy-state via window.VK_I18N (satt av vokshus-cart-upsell.liquid);
      // fallback til norsk literal så en manglende nøkkel aldri blokkerer kjøpet.
      const vkI18n = window.VK_I18N || {};
      btn.textContent = removeId
        ? (vkI18n.upsellUpgrading || 'Oppgraderer...')
        : (vkI18n.upsellAdding || 'Legger til...');

      try {
        // Resolve a handle → first variant id. Accessory products are created at
        // deploy; their ids aren't known to the theme, so look them up live.
        let variantTitle = '';
        if (!variantId && addHandle) {
          const pRes = await fetch(`/products/${addHandle}.js`, { credentials: 'same-origin' });
          if (!pRes.ok) throw new Error(`Product lookup failed: ${pRes.status}`);
          const product = await pRes.json();
          const v0 = product && product.variants && product.variants[0];
          variantId = v0 && v0.id;
          variantTitle = (v0 && v0.title) || '';
          if (!variantId) throw new Error(`No variant for handle ${addHandle}`);
        }

        // 1) Add the target variant (kit upgrade OR accessory add-on).
        // _PackingList (engelskt packinnehåll för SpeedFulfill) följer med när
        // knappen bär data-vk-packing — samma property som buy-formsen sätter.
        const addItem = { id: parseInt(variantId, 10), quantity: 1 };
        if (btn.dataset.vkPacking) {
          // Fristående wrap: mønstret ÄR varianttiteln. Stava ut det på engelska
          // så SpeedFulfill aldrig ser pekaren "pattern = variant title".
          addItem.properties = { _PackingList: resolvePackingList(btn.dataset.vkPacking, variantTitle) };
        }
        if (btn.dataset.vkSellingPlan) {
          addItem.selling_plan = parseInt(btn.dataset.vkSellingPlan, 10);
        }
        // Elias 3/11 2026: byte paket → paket med mønster (Black Month → Storfamilie).
        // Kundens valda mønster ligger på raden som byts ut. De kopieras (synliga fält +
        // mønsterblocket i _PackingList), annars packar lagret fel. Hittas inget mønster:
        // inget byte, kunden går till varans sida och väljer där.
        if (btn.dataset.vkCopyPatternsFrom) {
          const srcRes = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
          const srcCart = srcRes.ok ? await srcRes.json() : null;
          const src = srcCart && srcCart.items.find((it) => String(it.variant_id) === String(btn.dataset.vkCopyPatternsFrom));
          const props = (src && src.properties) || {};
          const block = String(props._PackingList || '').match(/—\s*(Pattern[\s\S]*)$/);
          const PATTERN_KEY = /^_?(?:Mønster|Mönster|Monster|Pattern|Muster|Patroon|Motif|Wzór|Vzor)\s*\d*$/i;
          const picked = Object.keys(props).filter((k) => PATTERN_KEY.test(k) && props[k]);
          if (!block || !picked.length) {
            if (btn.dataset.vkFallbackUrl) { window.location.href = btn.dataset.vkFallbackUrl; return; }
            throw new Error('No patterns on the swapped line');
          }
          addItem.properties = addItem.properties || {};
          picked.forEach((k) => { addItem.properties[k] = props[k]; });
          addItem.properties._PackingList = `${btn.dataset.vkPacking} — ${block[1].trim()}`;
        }
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [addItem] }),
        });
        if (!res.ok) throw new Error(`Add failed: ${res.status}`);

        // 2) Swap out ONE unit of the original line — never the whole line:
        //    qty≥2 means the customer wants several bags, and zeroing would
        //    silently delete the extras. If the cart can't be read we skip
        //    the remove entirely — both lines stay visible (same principle
        //    as add-first/remove-second).
        if (removeId && btn.hasAttribute('data-vk-remove-all')) {
          const cartRes = await fetch('/cart.js', {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          });
          if (cartRes.ok) {
            const cartData = await cartRes.json();
            const updates = {};
            cartData.items
              .filter((it) => String(it.variant_id) === String(removeId))
              .forEach((it) => { updates[it.key] = 0; });
            if (Object.keys(updates).length) {
              await fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates }),
              });
            }
          }
        } else if (removeId) {
          let newQty = null;
          try {
            const cartRes = await fetch('/cart.js', {
              credentials: 'same-origin',
              headers: { Accept: 'application/json' },
            });
            if (cartRes.ok) {
              const cartData = await cartRes.json();
              const line = cartData.items.find(
                (it) => String(it.variant_id) === String(removeId)
              );
              if (line) newQty = Math.max(0, line.quantity - removeQty);
            }
          } catch (_) {
            newQty = null;
          }
          if (newQty !== null) {
            await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ updates: { [removeId]: newQty } }),
            });
          }
        }

        const autoNote = btn.dataset.vkAutoNote;
        const container = findCartContainer();
        if (container) {
          const refreshed = await refreshSection(container);
          if (refreshed) {
            if (autoNote) showAutoNote(container, autoNote);
            return;
          }
        }
        // Fallback: hård reload om vi inte kunde refresha (t.ex. om upsell
        // klickas från en kontext där cart-items-component inte finns).
        window.location.reload();
      } catch (err) {
        console.error('Vokshus upsell failed:', err);
        btn.disabled = false;
        btn.textContent = originalText;
      }
    });

    autoSwap();
    // Korgen ritas om av Horizon (lådan, antal, borttag) utan någon händelse vi äger,
    // så vi tittar efter en ny auto-knapp varje gång DOM:en ändras.
    if (window.MutationObserver) {
      let pending = false;
      new MutationObserver(() => {
        if (pending) return;
        pending = true;
        setTimeout(() => { pending = false; autoSwap(); }, 150);
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  // En gång per sidvisning och kombination (variant + antal): misslyckas bytet blir
  // knappen kvar för ett vanligt klick, i stället för att vi försöker om och om igen.
  const autoTried = new Set();
  function autoSwap() {
    const btn = document.querySelector('[data-vk-cart-upsell-btn][data-vk-auto-swap]');
    if (!btn || btn.disabled) return;
    const key = `${btn.dataset.variantId}:${btn.dataset.vkRemoveQty}`;
    if (autoTried.has(key)) return;
    autoTried.add(key);
    btn.click();
  }

  function showAutoNote(container, text) {
    const note = document.createElement('p');
    note.className = 'vk-cart-upsell__auto-note';
    note.setAttribute('role', 'status');
    note.textContent = text;
    container.insertBefore(note, container.firstChild);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
